<!-- ImprintLocationSelector.vue - Component for selecting imprint location -->
<template>
  <!-- Hide entire component for Envelop products -->
  <div v-if="!isHiddenForEnvelop" class="imprint-location-selector">
    <!-- Section Label with optional help icon -->
    <div class="section-header">
      <label class="section-label">{{ label }}</label>
      <button 
        v-if="showHelp && helpText" 
        class="help-icon"
        @click="toggleHelp"
        :title="helpIconTitle"
      >
        ?
      </button>
    </div>

    <!-- Help Text (collapsible) -->
    <transition name="slide-fade">
      <div v-if="isHelpVisible && helpText" class="help-text">
        {{ helpText }}
      </div>
    </transition>

    <!-- Location Dropdown -->
    <div class="location-dropdown-wrapper">
      <select
        v-model="selectedLocationValue"
        class="location-dropdown"
        :class="{ 'has-error': showErrorState }"
        :disabled="disabled || availableLocations.length === 0"
        @change="handleLocationChange"
      >
        <option value="" disabled>{{ placeholder }}</option>
        <option
          v-for="location in availableLocations"
          :key="location.sku"
          :value="location.sku"
          :disabled="isLocationUsedInOtherImprints(location.sku)"
        >
          {{ location.title }}
          <span v-if="isLocationUsedInOtherImprints(location.sku) && location.sku !== selectedLocationValue"> (Already Used)</span>
        </option>
      </select>
      <span class="dropdown-icon">▼</span>
    </div>

    <!-- Error Message -->
    <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
    <p v-if="locationError" class="error-message">{{ locationError }}</p>

    <!-- No Locations Available Message -->
    <p v-if="availableLocations.length === 0 && !loading" class="info-message">
      Please select an imprint method first to see available locations.
    </p>

    <!-- Loading State -->
    <p v-if="loading" class="info-message">
      Loading locations...
    </p>
  </div>
</template>

<script>
import { useImprintStore } from '../stores/useImprintStore';
import { useNewImprintStore } from '../stores/useNewImprintStore';
import { useAppStore } from '../stores/useAppStore';

export default {
  name: 'ImprintLocationSelector',

  emits: ['location-selected', 'update:modelValue'],

  props: {
    // Label text
    label: {
      type: String,
      default: '2. Choose an Imprint Location'
    },
    
    // Placeholder for dropdown
    placeholder: {
      type: String,
      default: 'Location Name'
    },
    
    // Initial selected location SKU
    initialLocation: {
      type: String,
      default: ''
    },
    
    // Imprint key (index) for validation
    imprintKey: {
      type: Number,
      default: null
    },
    
    // Show help icon
    showHelp: {
      type: Boolean,
      default: false
    },
    
    // Help text content
    helpText: {
      type: String,
      default: 'Select where you want the imprint to appear on the product.'
    },
    
    // Help icon title (tooltip)
    helpIconTitle: {
      type: String,
      default: 'Show help'
    },
    
    // Disabled state
    disabled: {
      type: Boolean,
      default: false
    },
    
    // Loading state
    loading: {
      type: Boolean,
      default: false
    },
    
    // Error state
    hasError: {
      type: Boolean,
      default: false
    },
    
    // Error message
    errorMessage: {
      type: String,
      default: ''
    },
    
    // Auto-select first location
    autoSelectFirst: {
      type: Boolean,
      default: true
    },
    
    // Required field (show error if empty)
    required: {
      type: Boolean,
      default: true
    }
  },

  setup() {
    const imprintStore = useImprintStore();
    const newImprintStore = useNewImprintStore();
    const appStore = useAppStore();
    
    return {
      imprintStore,
      newImprintStore,
      appStore
    };
  },

  data() {
    return {
      selectedLocationValue: this.initialLocation,
      isHelpVisible: false,
      isMounted: false, // Track if component is mounted
      locationError: null // Validation error for duplicate locations
    };
  },

  computed: {
    // Get available locations from Pinia store
    availableLocations() {
      const locations = this.imprintStore.availableLocations;
      console.log('[ImprintLocationSelector] Available locations:', locations);
      return locations || [];
    },
    
    // Get selected location object
    selectedLocationObject() {
      if (!this.selectedLocationValue) return null;
      return this.availableLocations.find(loc => loc.sku === this.selectedLocationValue);
    },
    
    // Check if should show error state (red border)
    showErrorState() {
      // Show error if:
      // 1. hasError prop is true (external validation)
      // 2. locationError exists (duplicate location)
      // 3. Required field is empty (except for Envelop products)
      // For Envelop products, location is not required
      if (this.appStore.isEnvelopProduct) {
        return this.hasError || !!this.locationError;
      }
      return this.hasError || 
             !!this.locationError || 
             (this.required && !this.selectedLocationValue);
    },
    
    /**
     * Check if this selector should be hidden
     * Envelop products do not have location selection
     */
    isHiddenForEnvelop() {
      return this.appStore.isEnvelopProduct;
    }
  },

  methods: {
    toggleHelp() {
      this.isHelpVisible = !this.isHelpVisible;
    },
    
    /**
     * Check if location is used in OTHER imprints (not current)
     */
    isLocationUsedInOtherImprints(locationSku) {
      // Use imprintKey prop if provided, otherwise fallback to currentImprintKey
      const currentKey = this.imprintKey !== null 
        ? this.imprintKey 
        : this.newImprintStore.currentImprintKey;
      
      return this.newImprintStore.isLocationUsedInOtherImprints(
        locationSku,
        currentKey
      );
    },
    
    handleLocationChange() {
      const location = this.selectedLocationObject;
      
      console.log('[ImprintLocationSelector] Location selected:', {
        sku: this.selectedLocationValue,
        location: location,
        imprintKey: this.imprintKey
      });
      
      // Use imprintKey prop if provided, otherwise fallback to currentImprintKey
      const currentKey = this.imprintKey !== null 
        ? this.imprintKey 
        : this.newImprintStore.currentImprintKey;
      
      // Validate location selection
      const validation = this.newImprintStore.validateLocationSelection(
        this.selectedLocationValue,
        currentKey
      );
      
      if (!validation.valid) {
        // Show error message
        this.locationError = validation.error;
        
        console.warn('[ImprintLocationSelector] Validation failed:', validation.error);
        
        // Reset selection
        this.selectedLocationValue = '';
        
        return;
      }
      
      // Clear error
      this.locationError = null;
      
      // Emit location change event
      this.$emit('location-selected', {
        sku: this.selectedLocationValue,
        location: location
      });
      
      // Emit update:modelValue for v-model support
      this.$emit('update:modelValue', this.selectedLocationValue);
    },
    
    // Public method to set location programmatically
    setLocation(locationSku) {
      this.selectedLocationValue = locationSku;
      this.handleLocationChange();
    },
    
    // Public method to clear selection
    clearSelection() {
      this.selectedLocationValue = '';
      this.$emit('location-selected', null);
      this.$emit('update:modelValue', '');
    }
  },

  watch: {
    initialLocation: {
      immediate: true,
      handler(newValue) {
        console.log('[ImprintLocationSelector] initialLocation changed:', {
          newValue,
          imprintKey: this.imprintKey,
          currentValue: this.selectedLocationValue
        });
        
        // Update selectedLocationValue whenever initialLocation prop changes
        // This ensures component stays in sync with parent data
        this.selectedLocationValue = newValue || '';
        
        // Clear error when location changes
        this.locationError = null;
      }
    },
    
    // Watch for locations becoming available
    availableLocations: {
      handler(newLocations) {
        // Only auto-select if component is mounted to avoid emit errors
        if (!this.isMounted) {
          console.log('[ImprintLocationSelector] Not mounted yet, skipping auto-select');
          return;
        }
        
        console.log('[ImprintLocationSelector] availableLocations changed:', {
          locationsCount: newLocations?.length,
          selectedLocationValue: this.selectedLocationValue,
          autoSelectFirst: this.autoSelectFirst,
          imprintKey: this.imprintKey
        });
        
        // Auto-select first AVAILABLE (not used) location if enabled and no selection
        if (this.autoSelectFirst && 
            newLocations && 
            newLocations.length > 0 && 
            !this.selectedLocationValue) {
          
          // Find first location that is NOT used in other imprints
          const firstAvailable = newLocations.find(loc => 
            !this.isLocationUsedInOtherImprints(loc.sku)
          );
          
          if (firstAvailable) {
            this.selectedLocationValue = firstAvailable.sku;
            this.handleLocationChange();
            
            console.log('[ImprintLocationSelector] Auto-selected first available location:', firstAvailable);
          } else {
            console.warn('[ImprintLocationSelector] No available locations to auto-select (all are used)');
          }
        } else {
          console.log('[ImprintLocationSelector] Skipping auto-select:', {
            reason: !this.autoSelectFirst ? 'autoSelectFirst=false' : 
                    !newLocations ? 'no locations' :
                    newLocations.length === 0 ? 'empty locations' :
                    this.selectedLocationValue ? 'already selected' : 'unknown'
          });
        }
      }
    }
  },

  mounted() {
    console.log('[ImprintLocationSelector] Mounted', {
      availableLocations: this.availableLocations.length,
      selectedLocation: this.selectedLocationValue,
      autoSelectFirst: this.autoSelectFirst
    });
    
    // Mark as mounted
    this.isMounted = true;
    
    // Trigger auto-select after mounting
    if (this.autoSelectFirst && 
        this.availableLocations && 
        this.availableLocations.length > 0 && 
        !this.selectedLocationValue) {
      
      // Find first location that is NOT used in other imprints
      const firstAvailable = this.availableLocations.find(loc => 
        !this.isLocationUsedInOtherImprints(loc.sku)
      );
      
      if (firstAvailable) {
        this.selectedLocationValue = firstAvailable.sku;
        this.handleLocationChange();
        
        console.log('[ImprintLocationSelector] Auto-selected first available location on mount:', firstAvailable);
      } else {
        console.warn('[ImprintLocationSelector] No available locations to auto-select on mount (all are used)');
      }
    }
  }
};
</script>

<style scoped lang="scss">
@use '@/styles/utils' as *;
.imprint-location-selector {
  //margin-bottom: 20px;
  width: 100%;
}

// Section Header
.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.section-label {
  display: block;
  font-size: 16px;
  font-weight: 400;
  color: #000;
  margin: 0;
}

.help-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid #333333;
  background: transparent;
  color: #333333;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.2s ease;
  display: none;
  
  &:hover {
    background: #333333;
    color: white;
  }
}

// Help Text
.help-text {
  margin-bottom: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 14px;
  color: #666;
  line-height: 1.4;
}

// Slide fade transition
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

// Location Dropdown
.location-dropdown-wrapper {
  position: relative;
  width: 100%;
}

.location-dropdown {
  width: 100%;
  padding: 10px;
  padding-right: 35px; // Space for dropdown icon
  font-size: 16px;
  font-family: $font-family-base;
  color: $color-black;
  border: 1px solid $color-border;
  border-radius: 5px;
  background: $color-white;
  appearance: none;
  cursor: pointer;
  transition: border-color 0.2s ease;
  
  // Placeholder styling
  option[value=""] {
    color: $color-text-light;
  }
  
  // Error state
  &.has-error {
    border-color: $color-danger;
  }
  
  // Focus state
  &:focus {
    outline: none;
    border-color: $brand-primary;
  }
  
  // Disabled state
  &:disabled {
    background: $color-bg-light;
    color: $color-gray;
    cursor: not-allowed;
  }
  
  // Selected value styling
  &:not(:disabled):not([value=""]) {
    color: $color-black;
  }
  
  // Disabled options styling
  option:disabled {
    color: $color-gray;
    font-style: italic;
  }
}

.dropdown-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #BEBDBD;
  font-size: 12px;
}

// Error Message
.error-message {
  margin: 4px 0 0 0;
  font-size: 12px;
  color: $color-danger;
  line-height: 1.4;
}

// Info Message
.info-message {
  margin: 8px 0 0 0;
  font-size: 14px;
  color: $color-text-light;
  font-style: italic;
  line-height: 1.4;
}
</style>
